import PropTypes from "prop-types";
import CustomDialog from "../../common/CustomDialog";
import { Box, CircularProgress, FormControl, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import AlertService from "../../common/Alert/alertService";
import { addNewClientSchema } from "../../../schemas/clientSchema";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import theme from "../../../theme";
import { clientTypeOptions } from "../../../constants/client.constants";
import { ClientActions, FormActions, SubscriptionsActions } from "../../api/RequestUtil";
import { ErrorMessage, getUtcDateTimeString, splitUtcStringIntoDateAndTime } from "../../../utils/commonUtils";
import MultiSelectDropdown from "../../common/FormField/MultiSelectDropdown";
import SubscriptionChipEditor from "../SubscriptionChipEditor";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { SUCCESS } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";

const InitiateOnboardingModal = ({ open, handleClose, email }) => {
  const { timezone } = useSelector((state) => state.userSettings);
  const [subscriptions, setSubscriptions] = useState({ data: [], loading: false });
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [subscriptionCustomizations, setSubscriptionCustomizations] = useState({});
  const [feedbackFormsOptions, setFeedbackFormsOptions] = useState([]);
  const [hasEwayCredentials, setHasEwayCredentials] = useState(false); // Default to false until confirmed
  const { success, error } = AlertService();
  const [loader, setLoader] = useState({ getData: false, setData: false });
  const navigate = useNavigate();
  const {
    handleSubmit,
    reset,
    watch,
    trigger,
    setValue: setFormValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(addNewClientSchema),
    defaultValues: {
      email,
      clientType: "",
      subscriptions: [],
      feedbackForms: [],
    },
  });

  const fetchSubscriptionsData = () => {
    setSubscriptions((prev) => ({ ...prev, loading: true }));
    setLoader((prev) => ({ ...prev, getData: true }));
    SubscriptionsActions.getSubscriptionsData({ excludeInactive: true })
      .then(({ data }) => {
        setSubscriptions({ data: data.data, loading: false });
      })
      .catch((data) => {
        error(ErrorMessage(data));
        setSubscriptions((prev) => ({ ...prev, loading: false }));
      })
      .finally(() => setLoader((prev) => ({ ...prev, getData: false })));
  };

  const fetchFeedbackFormsData = () => {
    const payloadData = {
      type: "feedback",
    };
    FormActions.getFormsData(payloadData)
      .then(({ data }) => {
        setFeedbackFormsOptions(data.data);
      })
      .catch((err) => {
        error(ErrorMessage(err.response?.data));
      });
  };

  const fetchEwayCredentials = () => {
    ClientActions.getEwayCredentialsEnvironment()
      .then((res) => {
        // Check if the user has valid eWay credentials
        // Backend returns { encryption_key, environment } directly or nested in data
        const hasCredentials =
          res.data?.encryption_key ||
          res.data?.data?.encryption_key ||
          res.data?.data?.userEwayCredentials?.encryptionKey ||
          res.data?.data?.userEwayCredentials?.publicApiKey;
        setHasEwayCredentials(!!hasCredentials);
      })
      .catch(() => {
        // If there's an error fetching credentials, assume no credentials
        setHasEwayCredentials(false);
      });
  };

  useEffect(() => {
    fetchSubscriptionsData();
    fetchFeedbackFormsData();
    fetchEwayCredentials();
  }, []);

  const watchedFeedbackForms = watch("feedbackForms");

  const handleSubscriptionRemove = (subscriptionId) => {
    const updatedSubscriptions = selectedSubscriptions.filter((id) => id !== subscriptionId);
    setSelectedSubscriptions(updatedSubscriptions);
    setFormValue("subscriptions", updatedSubscriptions);
    trigger("subscriptions");
    // Also remove customization
    const newCustomizations = { ...subscriptionCustomizations };
    delete newCustomizations[subscriptionId];
    setSubscriptionCustomizations(newCustomizations);
  };

  const handleSubscriptionChange = (subscriptionId, customization) => {
    setSubscriptionCustomizations({
      ...subscriptionCustomizations,
      [subscriptionId]: customization,
    });
  };

  const onSubmit = (values) => {
    const utcStartDateAndTime = getUtcDateTimeString(
      timezone,
      dayjs(new Date()).format("YYYY-MM-DD"),
      dayjs(new Date()).format("HH:mm")
    );

    const { time: utcTime } = splitUtcStringIntoDateAndTime(utcStartDateAndTime);

    // Map selected subscriptions with their customizations
    const subscriptionPayload = selectedSubscriptions.map((subId) => {
      const customization = subscriptionCustomizations[subId];
      if (customization) {
        // Include customized values
        return customization;
      }
      // Just include the subscription ID if not customized
      return { subscription_id: subId };
    });

    const payload = {
      email: values.email,
      client_type: values.clientType,
      subscriptions: subscriptionPayload,
      feedback_form: values.feedbackForms,
      utcTime,
    };
    setLoader((prev) => ({ ...prev, setData: true }));
    ClientActions.initiateNewLeadClient(payload)
      .then(({ data }) => {
        if (data.status === SUCCESS) {
          success(data.message);
          reset();
          setSelectedSubscriptions([]);
          setSubscriptionCustomizations({});
          handleClose();
          navigate(-1);
        }
      })
      .catch(({ data }) => {
        error(ErrorMessage(data));
      })
      .finally(() => setLoader((prev) => ({ ...prev, setData: false })));
  };

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      title="Initiate Onboarding"
      handleSave={handleSubmit(onSubmit)}
      loader={loader.setData}
    >
      {loader.getData ? (
        <Box display="flex" alignItems="center" justifyContent="center" height="22vh">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Stack component="form" gap={2}>
          <FormControl fullWidth>
            <Select
              value={watch("clientType")}
              onChange={(e) => {
                setFormValue("clientType", e.target.value);
                trigger("clientType");
              }}
              error={!!errors.clientType}
              IconComponent={KeyboardArrowDownIcon}
              defaultValue=""
              displayEmpty
            >
              <MenuItem value="" disabled>
                <Typography
                  color={theme.palette.gray.light}
                  height="100%"
                  display="flex"
                  alignItems="center"
                  fontSize={16}
                >
                  Client Type
                </Typography>
              </MenuItem>
              {clientTypeOptions.map((type) => (
                <MenuItem value={type.value} key={type.value}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
              {errors.clientType?.message}
            </Typography>
          </FormControl>

          {/* Subscription Selection */}
          <Grid item xs={12}>
            <MultiSelectDropdown
              data={subscriptions.data}
              options={subscriptions.data.filter((sub) => !selectedSubscriptions.includes(sub.id)).map((sub) => sub.id)}
              selectedValues={selectedSubscriptions}
              setSelectedValues={(newSubs) => {
                setSelectedSubscriptions(newSubs);
                setFormValue("subscriptions", newSubs);
                trigger("subscriptions");
              }}
              placeholder="Subscriptions"
              loading={subscriptions.loading}
              onChange={(event, newValue) => {
                setSelectedSubscriptions(newValue);
                setFormValue("subscriptions", newValue);
                trigger("subscriptions");
              }}
              renderChips={false}
            />
            {errors.subscriptions && (
              <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
                {errors.subscriptions.message}
              </Typography>
            )}
            {/* Editable Subscription Accordions */}
            {selectedSubscriptions.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {selectedSubscriptions.map((subId) => {
                  const subscription = subscriptions.data.find((s) => s.id === subId);
                  if (!subscription) return null;

                  return (
                    <SubscriptionChipEditor
                      key={subId}
                      subscription={subscription}
                      onRemove={handleSubscriptionRemove}
                      onChange={handleSubscriptionChange}
                      initialValues={subscriptionCustomizations[subId] || {}}
                      hasEwayCredentials={hasEwayCredentials}
                    />
                  );
                })}
              </Box>
            )}
          </Grid>

          {/* Feedback Forms Selection */}
          <Grid item xs={12}>
            <MultiSelectDropdown
              data={feedbackFormsOptions}
              options={feedbackFormsOptions
                .filter((form) => !watchedFeedbackForms.includes(form.id))
                .map((form) => form.id)}
              selectedValues={watchedFeedbackForms}
              setSelectedValues={(newForms) => {
                setFormValue("feedbackForms", newForms);
                trigger("feedbackForms");
              }}
              error={errors.feedbackForms?.message}
              placeholder="Feedback Forms"
              onChange={(event, newValue) => {
                setFormValue("feedbackForms", newValue);
                trigger("feedbackForms");
              }}
            />
          </Grid>
        </Stack>
      )}
    </CustomDialog>
  );
};

InitiateOnboardingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
};

export default InitiateOnboardingModal;
